import React, { useCallback, useMemo } from 'react';
import { DndProvider, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import WidgetPalette, { WIDGET_DND_TYPE } from './WidgetPalette';
import WidgetContainer from './WidgetContainer';
import GridItem from './GridItem';
import widgetRegistry from './widgetRegistry';
import useDashboardLayout from './useDashboardLayout';
import useContainerWidth from './useContainerWidth';
import useSelectedMeter from './useSelectedMeter';
import { compactWidgets, gridCellSize } from './gridUtils';
import { getVariantMeta } from './variantFamilies';

const GRID_COLS = 12;
const ROW_HEIGHT = 80;
const MARGIN = [16, 16];
const NEW_WIDGET_SORT_Y = 9999;

const DashboardCanvas = ({ widgets, meterId, onDropWidget, onDragEnd, onResizeEnd, onRemoveWidget, onUpdateWidget }) => {
    const [containerRef, containerWidth] = useContainerWidth();
    const colWidth = gridCellSize(containerWidth, GRID_COLS, MARGIN[0]);
    const metrics = { colWidth, rowHeight: ROW_HEIGHT, marginX: MARGIN[0], marginY: MARGIN[1] };

    const gridHeight = useMemo(() => {
        if (!widgets.length) return 0;
        const maxBottom = Math.max(...widgets.map((w) => w.layout.y + w.layout.h));
        return maxBottom * (ROW_HEIGHT + MARGIN[1]);
    }, [widgets]);

    const [{ isOver }, dropConnector] = useDrop(() => ({
        accept: WIDGET_DND_TYPE,
        drop: (item, monitor) => {
            const offset = monitor.getClientOffset();
            const bounds = containerRef.current?.getBoundingClientRect();
            if (!offset || !bounds || !colWidth) {
                onDropWidget(item.widgetType, undefined, item.variantId);
                return;
            }
            const x = Math.max(0, Math.floor((offset.x - bounds.left) / (colWidth + MARGIN[0])));
            const y = Math.max(0, Math.floor((offset.y - bounds.top) / (ROW_HEIGHT + MARGIN[1])));
            onDropWidget(item.widgetType, { x, y }, item.variantId);
        },
        collect: (monitor) => ({ isOver: monitor.isOver() }),
    }), [onDropWidget, colWidth, containerRef]);

    const setCanvasRef = useCallback((node) => {
        containerRef.current = node;
        dropConnector(node);
    }, [containerRef, dropConnector]);

    return (
        <div className={`dashboard-canvas${isOver ? ' dashboard-canvas--drag-over' : ''}`} ref={setCanvasRef}>
            {widgets.length === 0 ? (
                <div className="dashboard-canvas__empty">
                    Drag a widget here, or click <i className="fas fa-plus"></i> in the library.
                </div>
            ) : (
                <div className="dashboard-grid" style={{ position: 'relative', height: gridHeight }}>
                    {colWidth > 0 && widgets.map((w) => (
                        <GridItem
                            key={w.id}
                            id={w.id}
                            layout={w.layout}
                            metrics={metrics}
                            cols={GRID_COLS}
                            minW={w.layout.minW}
                            minH={w.layout.minH}
                            onDragEnd={onDragEnd}
                            onResizeEnd={onResizeEnd}
                        >
                            <WidgetContainer config={w} meterId={meterId} onRemove={onRemoveWidget} onUpdateSettings={onUpdateWidget} />
                        </GridItem>
                    ))}
                </div>
            )}
        </div>
    );
};

const Dashboard = ({ persistMode = 'localStorage' }) => {
    const { widgets, setWidgets, isLoaded, resetToDefault } = useDashboardLayout({ persistMode });
    const { meters, selectedMeterId, setSelectedMeterId } = useSelectedMeter();

    const addWidget = useCallback((type, dropXY, variantId) => {
        const meta = widgetRegistry[type];
        if (!meta) return;
        const chosenVariant = variantId || meta.defaultVariant;
        const variantMeta = getVariantMeta(meta.variantFamily, chosenVariant);
        const layoutSpec = variantMeta?.layout || meta.defaultLayout;
        const title = variantMeta?.title || meta.defaultTitle;

        setWidgets((prev) => {
            const w = layoutSpec.w;
            const x = dropXY ? Math.max(0, Math.min(GRID_COLS - w, dropXY.x)) : 0;
            const y = dropXY ? dropXY.y : NEW_WIDGET_SORT_Y;

            const newWidget = {
                id: `widget-${type}-${Date.now()}`,
                type,
                title,
                variant: chosenVariant,
                layout: { x, y, w, h: layoutSpec.h, minW: layoutSpec.minW, minH: layoutSpec.minH },
                settings: { ...meta.defaultSettings },
                dataSource: { ...meta.defaultDataSource },
            };
            return compactWidgets([...prev, newWidget], GRID_COLS);
        });
    }, [setWidgets]);

    const addCustomWidget = useCallback((customConfig) => {
        const meta = widgetRegistry[customConfig.type];
        if (!meta) return;
        const variantMeta = getVariantMeta(meta.variantFamily, customConfig.variant);
        const layoutSpec = variantMeta?.layout || meta.defaultLayout;
        const w = Math.max(layoutSpec.minW || 1, Math.min(GRID_COLS, Number(customConfig.layout?.w) || layoutSpec.w));
        const h = Math.max(layoutSpec.minH || 1, Number(customConfig.layout?.h) || layoutSpec.h);

        setWidgets((prev) => {
            const newWidget = {
                id: `widget-custom-${Date.now()}`,
                type: customConfig.type,
                title: customConfig.title || meta.defaultTitle,
                variant: customConfig.variant,
                layout: { x: 0, y: NEW_WIDGET_SORT_Y, w, h, minW: layoutSpec.minW, minH: layoutSpec.minH },
                settings: { ...meta.defaultSettings, ...customConfig.settings },
                dataSource: { ...meta.defaultDataSource, ...customConfig.dataSource },
            };
            return compactWidgets([...prev, newWidget], GRID_COLS);
        });
    }, [setWidgets]);

    const removeWidget = useCallback((id) => {
        setWidgets((prev) => compactWidgets(prev.filter((w) => w.id !== id), GRID_COLS));
    }, [setWidgets]);

    const updateWidget = useCallback((id, updates) => {
        setWidgets((prev) => {
            const updated = prev.map((w) => {
                if (w.id !== id) return w;
                const meta = widgetRegistry[w.type];
                const variant = updates.variant ?? w.variant;
                const variantMeta = getVariantMeta(meta.variantFamily, variant);
                const layoutSpec = variantMeta?.layout || meta.defaultLayout;
                const width = Math.max(layoutSpec.minW, updates.layout?.w ?? layoutSpec.w);
                const height = Math.max(layoutSpec.minH, updates.layout?.h ?? layoutSpec.h);
                return {
                    ...w,
                    title: updates.title ?? w.title,
                    variant,
                    settings: { ...w.settings, ...updates.settings },
                    layout: {
                        ...w.layout,
                        w: Math.min(GRID_COLS, width),
                        h: height,
                        minW: layoutSpec.minW,
                        minH: layoutSpec.minH,
                    },
                };
            });
            return compactWidgets(updated, GRID_COLS);
        });
    }, [setWidgets]);

    const handleDragEnd = useCallback((id, { x, y }) => {
        setWidgets((prev) => {
            const updated = prev.map((w) => (w.id === id ? { ...w, layout: { ...w.layout, x, y } } : w));
            return compactWidgets(updated, GRID_COLS);
        });
    }, [setWidgets]);

    const handleResizeEnd = useCallback((id, { w, h }) => {
        setWidgets((prev) => {
            const updated = prev.map((widget) => (widget.id === id ? { ...widget, layout: { ...widget.layout, w, h } } : widget));
            return compactWidgets(updated, GRID_COLS);
        });
    }, [setWidgets]);

    if (!isLoaded) {
        return <div className="dashboard-loading">Loading dashboard…</div>;
    }

    return (
        <DndProvider backend={HTML5Backend}>
            <div className="custom-dashboard">
                <WidgetPalette
                    onAddWidget={(type, variantId) => addWidget(type, undefined, variantId)}
                    onCreateWidget={addCustomWidget}
                />
                <div className="custom-dashboard__main">
                    <div className="custom-dashboard__toolbar">
                        <div className="custom-dashboard__toolbar-left">
                            <span>{widgets.length} widget{widgets.length !== 1 ? 's' : ''}</span>
                            <label className="custom-dashboard__meter-select">
                                <span>Meter</span>
                                <select value={selectedMeterId} onChange={(e) => setSelectedMeterId(e.target.value)}>
                                    {meters.map((m) => (
                                        <option key={m.meter_id} value={m.meter_id}>{m.meter_name || m.meter_id}</option>
                                    ))}
                                </select>
                            </label>
                        </div>
                        <button type="button" className="custom-dashboard__reset" onClick={resetToDefault}>
                            Reset to default
                        </button>
                    </div>
                    <DashboardCanvas
                        widgets={widgets}
                        meterId={selectedMeterId}
                        onDropWidget={addWidget}
                        onDragEnd={handleDragEnd}
                        onResizeEnd={handleResizeEnd}
                        onRemoveWidget={removeWidget}
                        onUpdateWidget={updateWidget}
                    />
                </div>
            </div>
        </DndProvider>
    );
};

export default Dashboard;
